import { PlusIcon, TrashIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import {
  deleteCamera,
  deleteCustomer,
  deleteSite,
  disclaimNvr,
  deleteNVRIPSpeaker,
} from "lib/actions";
import { useNavigate } from "react-router-dom";
import { showToast } from "ui/components/common";

export function BtnCreateCustomer({ btnTitle }: { btnTitle?: string }) {
  return (
    <Link
      to="/dashboard/customers/create"
      className="flex h-10 items-center rounded-2xl bg-blue-600 px-3 text-sm font-medium text-white transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
    >
      <PlusIcon className="h-5" />
      {btnTitle && btnTitle.length > 0 && (
        <span className="hidden md:block ml-3">{btnTitle}</span>
      )}
    </Link>
  );
}

export function BtnVisitCustomer({ orgId }: { orgId: string }) {
  return (
    <Link
      to={`/dashboard/customers/${orgId}/sites`}
      className="rounded-md border p-2 hover:bg-gray-100"
    >
      <EyeIcon className="w-5" />
    </Link>
  );
}

export function BtnDeleteCustomer({ orgId }: { orgId: string }) {
  const navigate = useNavigate();

  async function handleClick(event: any) {
    event.preventDefault();
    try {
      const response = await deleteCustomer(orgId);
      if (response) {
        navigate(`/dashboard/customers`);
      }
    } catch (error) {
      console.error(error);
      navigate(`?modal=true&content=${error}`);
    }
  }

  return (
    <button className="rounded-md p-2 hover:bg-gray-100" onClick={handleClick}>
      <span className="sr-only">Delete</span>
      <TrashIcon className="w-5" />
    </button>
  );
}

export function BtnCreateSite({ orgId }: { orgId: string }) {
  return (
    <Link
      to={`/dashboard/customers/${orgId}/sites/create`}
      className="flex h-10 items-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
    >
      <span className="hidden md:block">Create Site</span>{" "}
      <PlusIcon className="h-5 md:ml-4" />
    </Link>
  );
}

export function BtnVisitSite({
  orgId,
  siteId,
}: {
  orgId: string;
  siteId: string;
}) {
  return (
    <Link
      to={`/dashboard/customers/${orgId}/sites/${siteId}/nvrs`}
      className="rounded-md border p-2 hover:bg-gray-100"
    >
      <EyeIcon className="w-5" />
    </Link>
  );
}

export function BtnDeleteSite({
  orgId,
  siteId,
}: {
  orgId: string;
  siteId: string;
}) {
  const navigate = useNavigate();

  async function handleClick(event: any) {
    event.preventDefault();
    try {
      const response = await deleteSite(orgId, siteId);
      navigate(0);
    } catch (error) {
      console.error(error);
      navigate(`?modal=true&content=${error}`);
    }
  }

  return (
    <button className="rounded-md p-2 hover:bg-gray-100" onClick={handleClick}>
      <span className="sr-only">Delete</span>
      <TrashIcon className="w-5" />
    </button>
  );
}

export function BtnCreateNvr({
  orgId,
  siteId,
}: {
  orgId: string;
  siteId: string;
}) {
  return (
    <Link
      to={`/dashboard/customers/${orgId}/sites/${siteId}/nvrs/create`}
      className="flex h-10 items-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
    >
      <span className="hidden md:block">Create NVR</span>{" "}
      <PlusIcon className="h-5 md:ml-4" />
    </Link>
  );
}

export function BtnVisitNvr({
  orgId,
  siteId,
  nvrId,
}: {
  orgId: string;
  siteId: string;
  nvrId: string;
}) {
  return (
    <Link
      to={`/dashboard/customers/${orgId}/sites/${siteId}/nvrs/${nvrId}/cameras`}
      className="rounded-md border p-2 hover:bg-gray-100"
    >
      <EyeIcon className="w-5" />
    </Link>
  );
}

export function BtnDeleteNvr({
  orgId,
  siteId,
  nvrId,
}: {
  orgId: string;
  siteId: string;
  nvrId: string;
}) {
  const navigate = useNavigate();

  async function handleClick(event: any) {
    event.preventDefault();
    try {
      const response = await disclaimNvr(orgId, siteId, nvrId);
      navigate(0);
    } catch (error) {
      console.error(error);
      navigate(`?modal=true&content=${error}`);
    }
  }

  return (
    <button className="rounded-md p-2 hover:bg-gray-100" onClick={handleClick}>
      <span className="sr-only">Delete</span>
      <TrashIcon className="w-5" />
    </button>
  );
}

export function BtnCreateCamera({
  orgId,
  siteId,
  nvrId,
}: {
  orgId: string;
  siteId: string;
  nvrId: string;
}) {
  return (
    <Link
      to={`/dashboard/customers/${orgId}/sites/${siteId}/nvrs/${nvrId}/cameras/create`}
      className="flex h-10 items-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
    >
      <span className="hidden md:block">Create Camera</span>{" "}
      <PlusIcon className="h-5 md:ml-4" />
    </Link>
  );
}

export function BtnVisitCamera({
  orgId,
  siteId,
  nvrId,
  cameraId,
}: {
  orgId: string;
  siteId: string;
  nvrId: string;
  cameraId: string;
}) {
  return (
    <Link
      to={`/dashboard/customers/${orgId}/sites/${siteId}/nvrs/${nvrId}/cameras/${cameraId}`}
      className="rounded-md border p-2 hover:bg-gray-100"
    >
      <EyeIcon className="w-5" />
    </Link>
  );
}

export function BtnDeleteCamera({
  orgId,
  cameraId,
}: {
  orgId: string;
  cameraId: string;
}) {
  // const action = deleteCamera.bind(null, orgId, siteId, nvrId, cameraId);
  const navigate = useNavigate();

  async function handleClick(event: any) {
    event.preventDefault();
    try {
      const response = await deleteCamera(orgId, cameraId);
      navigate(0);
    } catch (error) {
      console.error(error);
      navigate(`?modal=true&content=${error}`);
    }
  }

  return (
    <button className="rounded-md p-2 hover:bg-gray-100" onClick={handleClick}>
      <span className="sr-only">Delete</span>
      <TrashIcon className="w-5" />
    </button>
  );
}

export function BtnDeleteIPSpeaker({
  orgId,
  id,
}: {
  orgId: string;
  id: string;
}) {
  const navigate = useNavigate();

  async function handleClick(event: any) {
    event.preventDefault();
    try {
      await deleteNVRIPSpeaker(orgId, id);
      showToast(0, "The IPSpeakers has been added successfully!", {
        minWidth: 482,
      });
      navigate(0);
    } catch (error) {
      console.error(error);
      navigate(`?modal=true&content=${error}`);
    }
  }

  return (
    <button className="rounded-md p-2 hover:bg-gray-100" onClick={handleClick}>
      <span className="sr-only">Delete</span>
      <TrashIcon className="w-5" />
    </button>
  );
}
